import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { buildCommands, buildSaveCommand, parseResponse } from '../core/commands';
import { COMMAND_DELAY_MS } from '../core/limits';
import type { CycleSettings, Program, SessionState } from '../core/types';
import { IDLE_SESSION } from '../core/types';
import { ManualTransport } from '../transport/manual';
import {
  DEFAULT_DEVICE_CONFIG,
  type BleLogEntry,
  type BleTransport,
  type ConnectionState,
  type DeviceConfig,
} from '../transport/types';
import { WebBluetoothTransport } from '../transport/webBluetooth';
import { KEYS, load, save } from './persist';

/** Why Web Bluetooth is unavailable here, in words the user can act on. */
export type PlatformNote = { supported: boolean; reason: string };

/**
 * iPhone or iPad, including an iPad reporting itself as a Mac.
 *
 * Note this is deliberately checked AFTER Web Bluetooth support in
 * detectPlatform(): Bluefy is an iOS browser that DOES implement it, and those
 * users should get the normal Bluetooth route rather than being pushed to Wi-Fi.
 */
export function isAppleMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (/Mac/.test(ua) && 'ontouchend' in document);
}

export function detectPlatform(): PlatformNote {
  if (typeof navigator === 'undefined') return { supported: false, reason: 'No browser.' };

  if (WebBluetoothTransport.isSupported()) {
    return { supported: true, reason: '' };
  }

  if (isAppleMobile()) {
    return {
      supported: false,
      reason:
        "Safari can't use Bluetooth — Apple has never allowed it. Use the device's own Wi-Fi instead.",
    };
  }
  return {
    supported: false,
    reason:
      'This browser does not support Web Bluetooth. Chrome or Edge on Android, Windows, macOS or Linux will work.',
  };
}

type SendProgress = { sent: number; total: number } | null;

type ConnectionStore = {
  state: ConnectionState;
  session: SessionState;
  bleLog: BleLogEntry[];
  platform: PlatformNote;
  deviceConfig: DeviceConfig;
  firmware: string | null;
  savedPrograms: string[];
  autorun: boolean;
  transportId: 'web-bluetooth' | 'manual';
  manualBuffer: string[];
  sending: SendProgress;
  lastError: string | null;
  /** Cycles read back from the device via SHOW, ready to open in the editor. */
  deviceProgram: { name: string; cycles: CycleSettings[] } | null;
  clearDeviceProgram: () => void;

  setTransport: (id: 'web-bluetooth' | 'manual') => void;
  setDeviceConfig: (c: DeviceConfig) => void;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  sendProgram: (program: Program, options?: { run?: boolean }) => Promise<void>;
  sendRaw: (command: string) => Promise<void>;
  clearBleLog: () => void;
  feedManual: (raw: string) => void;

  /* Device flash storage — spec §3.5. Programs live in ESP32 NVS and survive
     power-off, which is what makes AUTORUN and LAST useful. */
  runLast: () => Promise<void>;
  stop: () => Promise<void>;
  refreshStatus: () => Promise<void>;
  saveToDevice: (name: string) => Promise<void>;
  loadFromDevice: (name: string) => Promise<void>;
  deleteFromDevice: (name: string) => Promise<void>;
  listDevicePrograms: () => Promise<void>;
  setAutorun: (on: boolean) => Promise<void>;
};

const ConnectionContext = createContext<ConnectionStore | null>(null);

export function ConnectionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConnectionState>('disconnected');
  const [session, setSession] = useState<SessionState>(IDLE_SESSION);
  const [bleLog, setBleLog] = useState<BleLogEntry[]>([]);
  const [firmware, setFirmware] = useState<string | null>(null);
  const [savedPrograms, setSavedPrograms] = useState<string[]>([]);
  const [autorun, setAutorun] = useState(false);
  const [sending, setSending] = useState<SendProgress>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [transportId, setTransportId] = useState<'web-bluetooth' | 'manual'>(() =>
    WebBluetoothTransport.isSupported() ? 'web-bluetooth' : 'manual',
  );
  const [deviceConfig, setDeviceConfigState] = useState<DeviceConfig>(() =>
    load<DeviceConfig>(KEYS.device, DEFAULT_DEVICE_CONFIG),
  );
  const [manualBuffer, setManualBuffer] = useState<string[]>([]);
  const [deviceProgram, setDeviceProgram] = useState<{
    name: string;
    cycles: CycleSettings[];
  } | null>(null);
  // Cycles arrive one notification at a time, so they're gathered here until
  // the dump is complete rather than being rebuilt on every message.
  const showBuffer = useRef<CycleSettings[]>([]);
  const pendingLoadName = useRef<string>('DEVICE');

  const platform = useMemo(detectPlatform, []);


  useEffect(() => save(KEYS.device, deviceConfig), [deviceConfig]);

  const pushLog = useCallback((entry: BleLogEntry) => {
    setBleLog((prev) => [entry, ...prev].slice(0, 200));
  }, []);

  /*
   * Clear the session display when the link goes away.
   *
   * Session state only ever arrived from notifications, and nothing reset it,
   * so after a mid-session disconnect the visualiser carried on animating at
   * the last frequency it heard and the stats kept counting down from numbers
   * captured before the drop — while the device card said "Not connected"
   * two inches above. For an overnight session someone is supervising, a
   * display that looks live but is frozen is worse than no display: it is
   * indistinguishable from monitoring right up until it matters.
   *
   * Reconnecting keeps it, because a reconnect usually succeeds and the device
   * really is still running.
   */
  const handleState = useCallback((next: ConnectionState) => {
    setState(next);
    if (next === 'disconnected' || next === 'unsupported') setSession(IDLE_SESSION);
  }, []);

  const handleMessage = useCallback((raw: string) => {
    const parsed = parseResponse(raw);
    switch (parsed.kind) {
      case 'welcome':
        setFirmware(parsed.firmware);
        setAutorun(parsed.autorun);
        setSavedPrograms(parsed.saved);
        break;
      case 'list':
        setSavedPrograms(parsed.saved);
        break;
      case 'status':
        setSession(parsed.state);
        break;
      case 'sweep':
        setSession((s) => ({ ...s, freq: parsed.hz }));
        break;
      case 'phase':
        setSession((s) => ({
          ...s,
          running: true,
          phase: parsed.phase,
          cycle: parsed.cycle ?? s.cycle,
          totalCycles: parsed.totalCycles ?? s.totalCycles,
        }));
        break;
      case 'complete':
        setSession(IDLE_SESSION);
        break;
      case 'error':
        setLastError(parsed.text);
        break;
      case 'show-header':
        // A new dump is starting — discard whatever was half-collected before.
        showBuffer.current = [];
        break;
      case 'show-cycle':
        showBuffer.current[parsed.index] = parsed.cycle;
        // The device sends no end-of-dump marker, so publish after each line.
        // Any gaps mean a notification was lost; those entries stay undefined
        // and are filtered out below.
        setDeviceProgram({
          name: pendingLoadName.current,
          cycles: showBuffer.current.filter(Boolean),
        });
        break;
      default:
        break;
    }
  }, []);

  // The transport is held in a ref so reconnect callbacks keep working across
  // re-renders without tearing down the BLE connection.
  const transportRef = useRef<BleTransport | null>(null);

  const buildTransport = useCallback(
    (id: 'web-bluetooth' | 'manual'): BleTransport => {
      const events = {
        onState: handleState,
        onMessage: handleMessage,
        onLog: pushLog,
      };
      return id === 'web-bluetooth'
        ? new WebBluetoothTransport(deviceConfig, events)
        : new ManualTransport(events);
    },
    [deviceConfig, handleMessage, pushLog],
  );

  const getTransport = useCallback((): BleTransport => {
    if (!transportRef.current || transportRef.current.id !== transportId) {
      transportRef.current = buildTransport(transportId);
    }
    return transportRef.current;
  }, [buildTransport, transportId]);

  /*
   * Close the old transport before dropping the reference.
   *
   * Nulling the ref alone orphaned a live GATT connection that still held the
   * device's one connection slot AND still owned the onState/onMessage
   * callbacks. It went on feeding the session display, and when it eventually
   * dropped it flipped the UI into a reconnect loop — while the user was in
   * copy-and-paste mode and the app had already built a different transport.
   */
  const releaseTransport = useCallback(() => {
    const old = transportRef.current;
    transportRef.current = null;
    void old?.disconnect().catch(() => {
      // Already gone. Nothing to release.
    });
  }, []);

  const setTransport = useCallback(
    (id: 'web-bluetooth' | 'manual') => {
      releaseTransport();
      setTransportId(id);
      setState('disconnected');
      setSession(IDLE_SESSION);
    },
    [releaseTransport],
  );

  const setDeviceConfig = useCallback(
    (c: DeviceConfig) => {
      releaseTransport();
      setDeviceConfigState(c);
      setState('disconnected');
      setSession(IDLE_SESSION);
    },
    [releaseTransport],
  );

  const connect = useCallback(async () => {
    setLastError(null);
    try {
      await getTransport().connect();
    } catch (err) {
      setLastError(err instanceof Error ? err.message : String(err));
    }
  }, [getTransport]);

  const disconnect = useCallback(async () => {
    await transportRef.current?.disconnect();
  }, []);

  /*
   * Every caller of this is fired as `void conn.stop()` and friends, so an
   * unhandled rejection went nowhere: after the link dropped, tapping Stop
   * threw "Not connected." into the void — no error, no state change, nothing
   * on screen. Someone watching a session overnight would tap it, see the
   * button sit there unchanged, and reasonably conclude the device had stopped.
   *
   * Record the failure and re-throw, matching sendProgram.
   */
  const sendRaw = useCallback(
    async (command: string) => {
      setLastError(null);
      try {
        await getTransport().send(command);
      } catch (err) {
        setLastError(err instanceof Error ? err.message : String(err));
        throw err;
      }
    },
    [getTransport],
  );

  const sendProgram = useCallback(
    async (program: Program, options: { run?: boolean } = {}) => {
      const transport = getTransport();
      const cmds = buildCommands(program, options);
      setLastError(null);
      if (transport.id === 'manual') setManualBuffer([]);

      setSending({ sent: 0, total: cmds.length });
      try {
        for (let i = 0; i < cmds.length; i++) {
          await transport.send(cmds[i]);
          setSending({ sent: i + 1, total: cmds.length });
          // Spec §2.4: the firmware needs 250-300ms between commands.
          //
          // Only over a real radio. The manual transport just appends to an
          // array, so pacing it bought nothing and cost 15 seconds of staring
          // at a progress counter for a 7-step program — on the iPhone path,
          // which is the one that most needed to feel quick.
          if (i < cmds.length - 1 && transport.id !== 'manual') {
            await new Promise((r) => setTimeout(r, COMMAND_DELAY_MS));
          }
        }
        if (transport.id === 'manual') {
          setManualBuffer((transport as ManualTransport).buffer.slice());
        }
      } catch (err) {
        setLastError(err instanceof Error ? err.message : String(err));
        throw err;
      } finally {
        setSending(null);
      }
    },
    [getTransport],
  );

  const feedManual = useCallback((raw: string) => {
    const t = transportRef.current;
    if (t instanceof ManualTransport) t.receive(raw);
  }, []);

  const runLast = useCallback(() => sendRaw('LAST'), [sendRaw]);
  const stop = useCallback(() => sendRaw('STOP'), [sendRaw]);
  const refreshStatus = useCallback(() => sendRaw('STATUS'), [sendRaw]);
  const listDevicePrograms = useCallback(() => sendRaw('LIST'), [sendRaw]);

  const saveToDevice = useCallback(
    async (name: string) => {
      await sendRaw(buildSaveCommand(name));
    },
    [sendRaw],
  );

  const loadFromDevice = useCallback(
    async (name: string) => {
      pendingLoadName.current = name;
      showBuffer.current = [];
      setDeviceProgram(null);
      await sendRaw(`LOAD ${name}`);
      // SHOW returns one notification per cycle, which handleMessage collects
      // into deviceProgram for the editor.
      await new Promise((r) => setTimeout(r, COMMAND_DELAY_MS));
      await sendRaw('SHOW');
    },
    [sendRaw],
  );

  const deleteFromDevice = useCallback(
    async (name: string) => {
      await sendRaw(`DELETE ${name}`);
      await new Promise((r) => setTimeout(r, COMMAND_DELAY_MS));
      await sendRaw('LIST');
    },
    [sendRaw],
  );

  const setAutorunCmd = useCallback(
    async (on: boolean) => {
      await sendRaw(`AUTORUN ${on ? 'ON' : 'OFF'}`);
      setAutorun(on);
    },
    [sendRaw],
  );

  const value = useMemo<ConnectionStore>(
    () => ({
      state,
      session,
      bleLog,
      platform,
      deviceConfig,
      firmware,
      savedPrograms,
      autorun,
      transportId,
      manualBuffer,
      sending,
      lastError,
      deviceProgram,
      clearDeviceProgram: () => setDeviceProgram(null),
      setTransport,
      setDeviceConfig,
      connect,
      disconnect,
      sendProgram,
      sendRaw,
      clearBleLog: () => setBleLog([]),
      feedManual,
      runLast,
      stop,
      refreshStatus,
      saveToDevice,
      loadFromDevice,
      deleteFromDevice,
      listDevicePrograms,
      setAutorun: setAutorunCmd,
    }),
    [
      state,
      session,
      bleLog,
      platform,
      deviceConfig,
      firmware,
      savedPrograms,
      autorun,
      transportId,
      manualBuffer,
      sending,
      lastError,
      deviceProgram,
      setTransport,
      setDeviceConfig,
      connect,
      disconnect,
      sendProgram,
      sendRaw,
      feedManual,
      runLast,
      stop,
      refreshStatus,
      saveToDevice,
      loadFromDevice,
      deleteFromDevice,
      listDevicePrograms,
      setAutorunCmd,
    ],
  );

  return <ConnectionContext.Provider value={value}>{children}</ConnectionContext.Provider>;
}

export function useConnection(): ConnectionStore {
  const ctx = useContext(ConnectionContext);
  if (!ctx) throw new Error('useConnection must be used inside ConnectionProvider');
  return ctx;
}
